/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import { useTracker } from 'bundles/page/lib/event-pulse/react';
import { getUpsellCopy } from 'bundles/stackability-common';
import LearnMoreLink from 'bundles/unified-description-page-common/components/shared/LearnMoreLink';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

import ACELogo from 'images/unified-description-page/ace-logo.png';

import _t from 'i18n!nls/xdp';

type Props = {
  variantTitle: string;
  aceLink: string;
  disableCta?: boolean;
};

const styles = {
  container: () => css`
    display: flex;
    margin-top: var(--cds-spacing-400);
    padding-top: var(--cds-spacing-400);
    border-top: 2px solid var(--cds-color-grey-50);
  `,
  logoWrapper: () => css`
    margin-right: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  heading: () => css`
    margin-bottom: var(--cds-spacing-100);
  `,
  description: () => css`
    padding-right: var(--cds-spacing-50);
  `,
};

const ACECreditRecommendationInfo = ({ variantTitle, aceLink, disableCta }: Props) => {
  const eventingProductData = useEventingProductData();
  const track = useTracker();

  if (!variantTitle || !aceLink) {
    return null;
  }

  const upsellCopy = getUpsellCopy().homepagesNavXdp;

  return (
    <div css={styles.container}>
      <div css={styles.logoWrapper}>
        <LazyImgix src={ACELogo} alt={_t('ACE Logo')} maxWidth={60} maxHeight={60} height={60} width={60} />
      </div>
      <div>
        <Typography variant="h2semibold" component="h3" css={styles.heading}>
          {upsellCopy.degreeCreditEligible}
        </Typography>
        <Typography2 component="p">
          <span css={styles.description}>{upsellCopy.variantAceCreditRecommended(variantTitle)}</span>
          {!disableCta && (
            <LearnMoreLink
              href={aceLink}
              trackingName="ace_credit_learn_more"
              ariaLabel={_t('Learn more about ACE Recommendation')}
              target="_blank"
              onClick={() => {
                track('click_button', {
                  button: {
                    name: 'description_page_ace',
                  },
                  pageSection: {
                    sectionName: 'eligible_degree',
                  },
                  ...eventingProductData,
                });
              }}
            />
          )}
        </Typography2>
      </div>
    </div>
  );
};

export default ACECreditRecommendationInfo;
